import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminAPI } from "../../services/api";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const Reports = () => {
  const [reportType, setReportType] = useState("student-exam-scores");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [minScore, setMinScore] = useState("");
  const [maxScore, setMaxScore] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [fetchAll, setFetchAll] = useState(false);

  // Fetch student exam scores
  const {
    data: scoresData,
    refetch: refetchScores,
    isLoading: scoresLoading,
  } = useQuery({
    queryKey: ["admin-student-exam-scores", currentPage, pageSize, fetchAll],
    queryFn: async () => {
      try {
        const params = fetchAll ? { limit: 1000 } : { page: currentPage, limit: pageSize };
        const result = await adminAPI.getStudentExamScores(params);
        return result?.data?.data || result?.data || [];
      } catch (error) {
        console.error("Error fetching student exam scores:", error);
        throw error;
      }
    },
    enabled: reportType === "student-exam-scores",
  });

  // Auto-load scores when selecting the report type
  React.useEffect(() => {
    if (reportType === "student-exam-scores") {
      refetchScores();
    }
  }, [reportType, refetchScores]);

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Filter and process data
  const filteredData = useMemo(() => {
    if (!scoresData?.scores) return [];
    
    let filtered = scoresData.scores.filter(score => {
      // Filter out entries without participant name
      if (!score.participantName && !(score.participantFirstName && score.participantLastName)) {
        return false;
      }
      
      // Filter by date - FIXED LOGIC to properly include today's data
      if (score.completedAt) {
        const completedDate = new Date(score.completedAt);
        completedDate.setHours(0, 0, 0, 0); // Normalize to start of day
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Apply date filters
        if (dateFilter !== "all") {
          if (dateFilter === "today") {
            // For "today" filter, we want today's data
            if (completedDate.getTime() !== today.getTime()) return false;
          } else if (dateFilter === "week") {
            // Show data from the last 7 days including today
            const weekStart = new Date(today);
            weekStart.setDate(weekStart.getDate() - 6); // 7 days including today
            if (completedDate < weekStart || completedDate > today) return false;
          } else if (dateFilter === "month") {
            // Show data from the last 30 days including today
            const monthStart = new Date(today);
            monthStart.setDate(monthStart.getDate() - 29); // 30 days including today
            if (completedDate < monthStart || completedDate > today) return false;
          } else if (dateFilter === "custom") {
            // Custom date range
            if (customStartDate && completedDate < new Date(customStartDate)) return false;
            if (customEndDate && completedDate > new Date(customEndDate)) return false;
          }
        }
      }
      
      // Filter by score range
      if (minScore !== "" && score.percentage < parseFloat(minScore)) return false;
      if (maxScore !== "" && score.percentage > parseFloat(maxScore)) return false;
      
      // Filter by search term
      if (searchTerm) {
        const fullName = score.participantName || `${score.participantFirstName} ${score.participantLastName}`;
        if (!fullName.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      }
      
      return true;
    });
    
    return filtered;
  }, [scoresData, dateFilter, customStartDate, customEndDate, minScore, maxScore, searchTerm]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!filteredData.length) return { scoreRanges: [], passFailData: [] };
    
    // Score distribution
    const scoreRanges = [
      { name: "0-20%", count: 0 },
      { name: "21-40%", count: 0 },
      { name: "41-60%", count: 0 },
      { name: "61-80%", count: 0 },
      { name: "81-100%", count: 0 }
    ];
    
    filteredData.forEach(score => {
      if (score.percentage <= 20) scoreRanges[0].count++;
      else if (score.percentage <= 40) scoreRanges[1].count++;
      else if (score.percentage <= 60) scoreRanges[2].count++;
      else if (score.percentage <= 80) scoreRanges[3].count++;
      else scoreRanges[4].count++;
    });
    
    // Pass/Fail distribution
    const passFailData = [
      { name: "Pass", count: filteredData.filter(s => s.percentage >= 50).length },
      { name: "Fail", count: filteredData.filter(s => s.percentage < 50).length }
    ];
    
    return { scoreRanges, passFailData };
  }, [filteredData]);

  // Clear filters
  const clearFilters = () => {
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setMinScore("");
    setMaxScore("");
    setSearchTerm("");
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      // Check if user selected a custom date range
      if (dateFilter !== "custom" || !customStartDate || !customEndDate) {
        alert("Please select a custom date range (e.g., 12 Dec - 14 Dec) to export data.");
        setDateFilter("custom");
        return;
      }

      if (filteredData.length === 0) {
        alert("No data to export for the selected date range. Please adjust your filters.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Student Exam Scores");

      // Add main title header
       const titleRow = worksheet.addRow(["Exam Result"]);
       titleRow.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
       titleRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
       titleRow.alignment = { horizontal: "center", vertical: "center" };
       titleRow.height = 28;

       // Add date range header
       const startDateFormatted = new Date(customStartDate).toLocaleDateString("en-US", {
         month: "short",
         day: "numeric",
       });
       const endDateFormatted = new Date(customEndDate).toLocaleDateString("en-US", {
         month: "short",
         day: "numeric",
       });
       const dateRangeRow = worksheet.addRow([`${startDateFormatted} - ${endDateFormatted}`]);
       dateRangeRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
       dateRangeRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF366092" } };
       dateRangeRow.alignment = { horizontal: "center", vertical: "center" };
       dateRangeRow.height = 24;
       worksheet.addRow([]);

       // Add header row with styling
        const headerRow = worksheet.addRow([
          "Participant Name",
          "Email",
          "Position",
          "Educational Level",
          "Score",
          "Total Marks",
          "Status",
          "Completed At"
        ]);

      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF366092" } };
      headerRow.alignment = { horizontal: "center", vertical: "center" };

      // Sort data by skill level: Advanced, Intermediate, Beginner
      const getStatus = (marks) => marks <= 25 ? "Beginner" : marks <= 60 ? "Intermediate" : "Advanced";
      const sortedData = [...filteredData].sort((a, b) => {
        const statusA = getStatus(a.obtainedMarks || 0);
        const statusB = getStatus(b.obtainedMarks || 0);
        const statusOrder = { "Advanced": 0, "Intermediate": 1, "Beginner": 2 };
        return statusOrder[statusA] - statusOrder[statusB];
      });

      // Group data by status
      const groupedData = { "Advanced": [], "Intermediate": [], "Beginner": [] };
      sortedData.forEach((score) => {
        const status = getStatus(score.obtainedMarks || 0);
        groupedData[status].push(score);
      });

      // Add data rows grouped by category with belt separators
      const categoryOrder = ["Advanced", "Intermediate", "Beginner"];
      const categoryColors = {
        "Advanced": { bg: "FF1F4E78", text: "FFFFFFFF" }, // Dark Blue
        "Intermediate": { bg: "FF1F4E78", text: "FFFFFFFF" }, // Dark Blue
        "Beginner": { bg: "FF1F4E78", text: "FFFFFFFF" } // Dark Blue
      };

      categoryOrder.forEach((category) => {
        if (groupedData[category].length > 0) {
          // Add category header belt
          const categoryRow = worksheet.addRow([category.toUpperCase()]);
          categoryRow.font = { bold: true, size: 12, color: { argb: categoryColors[category].text } };
          categoryRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: categoryColors[category].bg } };
          categoryRow.alignment = { horizontal: "center", vertical: "center" };
          categoryRow.height = 20;

          // Add data rows for this category
          groupedData[category].forEach((score) => {
            const points = score.obtainedMarks || 0;
            const status = getStatus(points);
            const name = score.participantName || `${score.participantFirstName || ""} ${score.participantLastName || ""}`.trim() || "Unknown";
            const emailResponse = score.questionResponses?.find(q => q.questionText?.toLowerCase().includes("email"));
            const email = emailResponse?.essayAnswer || "N/A";
            const positionResponse = score.questionResponses?.find(q => q.questionText?.toLowerCase().includes("position") || q.questionText?.toLowerCase().includes("job"));
            const position = positionResponse?.essayAnswer || "N/A";
            const educationalLevelResponse = score.questionResponses?.find(q => q.questionText?.toLowerCase().includes("educational") || q.questionText?.toLowerCase().includes("education"));
            const educationalLevel = educationalLevelResponse?.essayAnswer || educationalLevelResponse?.selectedOptionDetails?.[0]?.text || "N/A";

            worksheet.addRow([
              name,
              email,
              position,
              educationalLevel,
              `${score.obtainedMarks || 0}`,
              `${score.totalMarks || 0}`,
              status,
              formatDate(score.completedAt)
            ]);
          });

          // Add spacing between categories
          worksheet.addRow([]);
        }
      });
      
      // Auto-fit columns
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellLength = cell.value ? cell.value.toString().length : 10;
          if (cellLength > maxLength) maxLength = cellLength;
        });
        column.width = Math.min(maxLength + 2, 50);
      });

      // Add Answer Statistics Sheet
       const statsSheet = workbook.addWorksheet("Answer Statistics");
       
       // Set column widths
       statsSheet.columns = [
         { width: 45, alignment: { horizontal: "left", wrapText: true } },
         { width: 25, alignment: { horizontal: "center" } }
       ];
       
       // Add title
       const statsTitleRow = statsSheet.addRow(["ANSWER STATISTICS"]);
       statsTitleRow.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
       statsTitleRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
       statsTitleRow.alignment = { horizontal: "center", vertical: "center" };
       statsTitleRow.height = 28;
       
       // Add border to title
       statsTitleRow.border = {
         top: { style: "thin", color: { argb: "FF000000" } },
         bottom: { style: "thin", color: { argb: "FF000000" } },
         left: { style: "thin", color: { argb: "FF000000" } },
         right: { style: "thin", color: { argb: "FF000000" } }
       };
       
       statsSheet.addRow([]);
       
       // Generate answer statistics
       try {
         if (scoresData?.scores && scoresData.scores.length > 0) {
           const firstScore = scoresData.scores[1];
           const questionResponses = firstScore.questionResponses;
           
           if (questionResponses && questionResponses.length > 0) {
             const allQuestions = questionResponses.slice(3, 18); // Skip first 3 questions
             let qNum = 1;
             
             allQuestions.forEach((q, qIndex) => {
               const actualIndex = qIndex + 3;
               
               // Only show questions that have selectedOptionDetails (same as Sheet 2)
               if (q.selectedOptionDetails && q.selectedOptionDetails.length > 0) {
                 const questionText = q.questionText;
                 
                 // Get all unique options for this question
                 const uniqueOptions = [...new Set(q.allOptions.map(opt => opt.text))];
                 
                 // Add question header row
                 const questionRow = statsSheet.addRow([`Q${qNum}: ${questionText}`]);
                 questionRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
                 questionRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF366092" } };
                 questionRow.alignment = { horizontal: "left", vertical: "top", wrapText: true };
                 questionRow.height = 45;
                 questionRow.border = {
                   top: { style: "thin", color: { argb: "FF000000" } },
                   bottom: { style: "thin", color: { argb: "FF000000" } },
                   left: { style: "thin", color: { argb: "FF000000" } },
                   right: { style: "thin", color: { argb: "FF000000" } }
                 };
                 
                 // Add column headers for options
                 const optionHeaderRow = statsSheet.addRow(["Answer Option", "Number of People"]);
                 optionHeaderRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
                 optionHeaderRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
                 optionHeaderRow.alignment = { horizontal: "center", vertical: "center" };
                 optionHeaderRow.height = 18;
                 optionHeaderRow.border = {
                   top: { style: "thin", color: { argb: "FF000000" } },
                   bottom: { style: "thin", color: { argb: "FF000000" } },
                   left: { style: "thin", color: { argb: "FF000000" } },
                   right: { style: "thin", color: { argb: "FF000000" } }
                 };
                 
                 let totalResponses = 0;
                 // Count each option from filtered data
                 const optionCounts = [];
                 uniqueOptions.forEach((option) => {
                   let count = 0;
                   filteredData.forEach((score) => {
                     if (score.questionResponses && score.questionResponses[actualIndex]) {
                       const qResp = score.questionResponses[actualIndex];
                       if (qResp.selectedOptionDetails) {
                         const selected = qResp.selectedOptionDetails.find(opt => opt.text === option);
                         if (selected) count++;
                       }
                     }
                   });
                   optionCounts.push({ option, count });
                   totalResponses += count;
                 });
                 
                 // Add answer option rows
                 optionCounts.forEach(({ option, count }) => {
                   const optRow = statsSheet.addRow([option, count]);
                   optRow.getCell(1).alignment = { horizontal: "left", vertical: "center", wrapText: true };
                   optRow.getCell(2).alignment = { horizontal: "center", vertical: "center" };
                   optRow.font = { size: 10 };
                   optRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
                   optRow.border = {
                     top: { style: "thin", color: { argb: "FFD9D9D9" } },
                     bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
                     left: { style: "thin", color: { argb: "FFD9D9D9" } },
                     right: { style: "thin", color: { argb: "FFD9D9D9" } }
                   };
                   optRow.height = 16;
                 });
                 
                 // Add total row
                 const totalRow = statsSheet.addRow(["Total Responses", totalResponses]);
                 totalRow.font = { bold: true, size: 11 };
                 totalRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFE699" } };
                 totalRow.getCell(1).alignment = { horizontal: "left", vertical: "center" };
                 totalRow.getCell(2).alignment = { horizontal: "center", vertical: "center" };
                 totalRow.border = {
                   top: { style: "thin", color: { argb: "FF000000" } },
                   bottom: { style: "thin", color: { argb: "FF000000" } },
                   left: { style: "thin", color: { argb: "FF000000" } },
                   right: { style: "thin", color: { argb: "FF000000" } }
                 };
                 totalRow.height = 18;
                 
                 // Add spacing
                 statsSheet.addRow([]);
                 
                 qNum++;
               }
             });
           }
         }
       } catch (error) {
         console.error("Error generating answer statistics:", error);
       }

       // Generate file
       const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      saveAs(blob, `exam-scores-${new Date().toISOString().split("T")[0]}.xlsx`);
      alert(`Exported ${filteredData.length} records successfully!`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert(`Failed to export data: ${error.message}`);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Exam Reports</h1>
        <p className="text-gray-600">View participant scores and performance</p>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Participant
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter name..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {/* Score Range Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Score (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
              placeholder="0"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Score (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={maxScore}
              onChange={(e) => setMaxScore(e.target.value)}
              placeholder="100"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        {/* Custom Date Range */}
        {dateFilter === "custom" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-3">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Clear Filters
            </button>
            <button
              onClick={() => setFetchAll(!fetchAll)}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                fetchAll ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {fetchAll ? 'Show Paginated' : 'Fetch All Data'}
            </button>
            <button
              onClick={exportToExcel}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              Export to Excel
            </button>
          </div>
          
          {/* Pagination Controls */}
          {!fetchAll && scoresData?.pagination && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {scoresData.pagination.totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, scoresData.pagination.totalPages))}
                disabled={currentPage === scoresData.pagination.totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
              >
                Next
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="ml-2 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10/page</option>
                <option value={20}>20/page</option>
                <option value={50}>50/page</option>
                <option value={100}>100/page</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Charts Section */}
      {filteredData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Score Distribution Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Score Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.scoreRanges}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#4f46e5" name="Number of Students" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Pass/Fail Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pass/Fail Rate</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.passFailData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {chartData.passFailData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={index === 0 ? "#10b981" : "#ef4444"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {filteredData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Total Attempts</div>
            <div className="text-2xl font-bold text-gray-900">
              {fetchAll ? filteredData.length : (scoresData?.pagination?.totalCount || filteredData.length)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Average Score</div>
            <div className="text-2xl font-bold text-gray-900">
              {filteredData.length > 0 ? Math.round(filteredData.reduce((sum, score) => sum + score.percentage, 0) / filteredData.length) : 0}%
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Pass Rate</div>
            <div className="text-2xl font-bold text-gray-900">
              {filteredData.length > 0 ? Math.round((filteredData.filter(s => s.percentage >= 50).length / filteredData.length) * 100) : 0}%
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm font-medium text-gray-500">Highest Score</div>
            <div className="text-2xl font-bold text-gray-900">
              {filteredData.length > 0 ? Math.max(...filteredData.map(s => s.percentage)) : 0}%
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {scoresLoading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Student Exam Scores Report */}
      {reportType === "student-exam-scores" && !scoresLoading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Student Exam Scores</h2>
            <div className="text-sm text-gray-500">
              {fetchAll ? (
                `Showing all ${filteredData.length} records`
              ) : (
                `Showing ${filteredData.length} of ${scoresData?.pagination?.totalCount || 0} records (Page ${currentPage} of ${scoresData?.pagination?.totalPages || 1})`
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participant Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((score, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {score.participantName || `${score.participantFirstName} ${score.participantLastName}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {score.participantEmail || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {score.obtainedMarks} / {score.totalMarks}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        score.percentage >= 70 
                          ? "bg-green-100 text-green-800" 
                          : score.percentage >= 50 
                            ? "bg-yellow-100 text-yellow-800" 
                            : "bg-red-100 text-red-800"
                      }`}>
                        {score.percentage}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(score.completedAt)}
                    </td>
                  </tr>
                ))}
                
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                      No exam scores found matching your filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;